import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTransition } from './TransitionContext';
import { Loop } from '@/hooks/use-loops';
import SharedElement from './SharedElement';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { MessageCircle, Heart, Repeat2, ActivitySquare, Send } from 'lucide-react';
import { useLoopActions, useLoopComments } from '@/hooks/use-loops';
import { useReblogActions } from '@/hooks/use-reblogs';
import { getThumbnailUrl } from '@/utils/imageUtils';
import { useToast } from '@/hooks/use-toast';

interface TransitionPostProps {
  isOpen: boolean;
  onClose: () => void;
  loop: Loop | null;
  className?: string;
}

const TransitionPost: React.FC<TransitionPostProps> = ({
  isOpen,
  onClose,
  loop,
  className
}) => {
  const { likeLoop, unlikeLoop, addComment, isCommenting } = useLoopActions();
  const { reblog, quote } = useReblogActions();
  const { toast } = useToast();
  const { activeItem } = useTransition();
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const { data: comments, isLoading: commentsLoading } = useLoopComments(loop?.id);

  // Add debug logging for TransitionPost
  React.useEffect(() => {
    console.log('TransitionPost render state:', { isOpen, loop: loop?.id });
  }, [isOpen, loop]);

  // If no loop is provided, don't render anything
  if (!loop) {
    console.log('TransitionPost: No loop provided');
    return null;
  }

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loop) return;
    
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  };

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowComments(true);
  };

  const handleReblog = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loop) return;
    
    reblog(loop.id);
    toast({
      description: "Relooped successfully",
    });
  };

  const handleTrail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loop) return;
    
    // Show trail dialog or navigate to trail
    // This is usually handled through more actions button, but now directly available
    toast({
      description: "Viewing post trail",
    });
    
    // Use the same action as the "View trail" link in the original component
    // TODO: Implement trail functionality if needed
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loop) return;
    
    if (newComment.trim()) {
      addComment({ loopId: loop.id, content: newComment });
      setNewComment('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
          {/* Backdrop with click handler to close */}
          <motion.div
            className="absolute inset-0 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Main post container */}
          <motion.div 
            className={cn(
              "relative w-full max-w-lg max-h-[90vh] overflow-y-auto",
              "flex flex-col bg-[#1A1A1B] text-[#D7DADC] rounded-lg",
              "z-10 shadow-xl",
              className
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute top-2 right-2 rounded-full p-1 z-20 bg-[#272729] hover:bg-[#343536]" 
              onClick={onClose}
            >
              <X size={20} />
            </Button>
            
            {/* Header section with user info */}
            <div className="relative p-4 border-b border-[#343536] flex items-center">
              <Avatar className="h-10 w-10 mr-3">
                <AvatarImage src={loop.user?.profile_image_url || ''} />
                <AvatarFallback>{(loop.user?.username || 'U')[0].toUpperCase()}</AvatarFallback>
              </Avatar>
              
              <div className="flex flex-col">
                <span className="font-medium">{loop.user?.display_name || loop.user?.username || 'Anonymous'}</span>
                <span className="text-xs text-[#818384]">
                  @{loop.user?.username || 'anonymous'} • 
                  {loop.created_at && formatDistanceToNow(new Date(loop.created_at), { addSuffix: true })}
                </span>
              </div>
            </div>
            
            {/* Content section */}
            <div className="px-4 py-3">
              <p className="whitespace-pre-wrap text-base">{loop.content}</p>
              
              {/* Media */}
              {(loop.image_url || loop.video_url) && (
                <div className="mt-3">
                  <div className="rounded-md border border-[#343536] overflow-hidden bg-[#151516]">
                    {loop.image_url ? (
                      <img 
                        src={loop.image_url} 
                        alt="Post media"
                        className="max-w-full w-full object-contain"
                      />
                    ) : loop.video_url ? (
                      <video 
                        src={loop.video_url}
                        poster={loop.thumbnail_url}
                        className="max-w-full w-full object-contain"
                        controls
                        playsInline
                      />
                    ) : null}
                  </div>
                </div>
              )}
            </div>
            
            {/* Engagement stats - shown as simple counters */}
            <div className="px-4 py-2 border-t border-[#343536] text-xs text-[#818384]">
              <div className="flex items-center space-x-4">
                <span>{loop.like_count || 0} likes</span>
                <span>{loop.comment_count || 0} comments</span>
                <span>{loop.reblog_count || 0} reposts</span>
              </div>
            </div>
            
            {/* Action buttons - styled to match screenshot */}
            <div className="px-3 py-2 border-t border-[#343536] grid grid-cols-4 gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className={cn(
                  "flex items-center justify-center gap-1 py-3",
                  loop.is_liked ? "text-[#FF4500]" : "text-[#D7DADC]"
                )}
                onClick={handleLike}
              >
                <Heart size={20} className={loop.is_liked ? "fill-[#FF4500]" : ""} />
                <span className="text-xs">Like</span>
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                onClick={handleComment}
              >
                <MessageCircle size={20} />
                <span className="text-xs">Comment</span>
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                onClick={handleReblog}
              >
                <Repeat2 size={20} />
                <span className="text-xs">Repost</span>
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                onClick={handleTrail}
              >
                <ActivitySquare size={20} />
                <span className="text-xs">Trail</span>
              </Button>
            </div>
            
            {/* Comments section - styled to match screenshot */}
            <div className="border-t border-[#343536]">
              <h3 className="p-4 font-medium">Comments</h3>
              
              {/* Add comment form */}
              <div className="px-4 mb-4">
                <form onSubmit={handleSubmitComment} className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 bg-[#272729] border-[#343536]"
                    disabled={isCommenting}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!newComment.trim() || isCommenting}
                    className="bg-[#272729] hover:bg-[#2D2D2F] text-[#D7DADC]"
                  >
                    <Send size={16} />
                  </Button>
                </form>
              </div>
              
              {/* Comments list */}
              <div className="space-y-3 px-4 pb-4 max-h-60 overflow-y-auto">
                {commentsLoading ? (
                  <div className="text-center text-[#818384] py-2">Loading comments...</div>
                ) : comments?.length === 0 ? (
                  <div className="text-center text-[#818384] py-4">
                    No comments yet. Be the first to comment!
                  </div>
                ) : (
                  <AnimatePresence>
                    {comments?.map((comment) => (
                      <motion.div
                        key={comment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-2"
                      >
                        <Avatar className="w-6 h-6 flex-shrink-0">
                          <AvatarImage src={getThumbnailUrl(comment.user.profile_image_url, 48)} />
                          <AvatarFallback className="text-xs">
                            {comment.user.display_name?.[0] || comment.user.username[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="bg-[#272729] rounded-lg p-2">
                            <div className="font-medium text-xs">
                              {comment.user.display_name || comment.user.username}
                            </div>
                            <p className="text-sm mt-1">{comment.content}</p>
                          </div>
                          <div className="text-xs text-[#818384] mt-1">
                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TransitionPost;
